import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/ui/use-toast';
import * as Api from '../api';

type ConfigStep =
  | 'access'
  | 'grading'
  | 'time'
  | 'certificate'
  | 'anti-cheating'
  | 'gamification'
  | 'worksheet';

interface UpdateConfigParams {
  id: string;
  step: ConfigStep;
  data: Record<string, unknown>;
}

const API_MAP: Record<ConfigStep, (id: string, data: Record<string, unknown>) => ReturnType<typeof Api.UpdateConfigAccess>> = {
  access: Api.UpdateConfigAccess,
  grading: Api.UpdateConfigGrading,
  time: Api.UpdateConfigTime,
  certificate: Api.UpdateConfigCertificate,
  'anti-cheating': Api.UpdateConfigAntiCheating,
  'gamification': Api.UpdateConfigGamification,
  'worksheet': Api.UpdateConfigWorksheet,
};

const useUpdateConfig = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, step, data }: UpdateConfigParams) => {
      const fn = API_MAP[step];
      const response = await fn(id, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['test', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['test', 'single', variables.id] });
      toast.success('Success', { description: 'Settings saved' });
    },
    onError: (error: unknown) => {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : 'Failed to save';
      toast.error('Error', { description: message });
    },
  });
};

export default useUpdateConfig;
